/**
 * titulos.ts — Categoria "Títulos" (10 modelos premium)
 *
 * Aberturas de seção: título, chapéu, texto de apoio e variações claras,
 * escuras, com selo e com divisor.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - 10 modelos novos (Studio, motor de modelos — Lote C)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { WEB, b } from './_shared';

const modelos: StudioPatternDefinition[] = [
  {
    key: 'titulo.centralizado',
    category: 'titulos',
    label: 'Título centralizado',
    description: 'Abertura simples, centralizada.',
    preview: 'heading',
    outputs: WEB,
    order: 1,
    build: () => [
      b('section.layered', { paddingY: 'md', align: 'center', maxWidth: '820px', motion: 'fade' }, [
        b('ui.title', { text: 'Um título que convence', size: 'lg', level: 'h2', align: 'center' }),
      ]),
    ],
  },
  {
    key: 'titulo.com-apoio',
    category: 'titulos',
    label: 'Título com texto de apoio',
    description: 'Título e uma frase explicativa abaixo.',
    preview: 'heading',
    outputs: WEB,
    order: 2,
    build: () => [
      b('section.layered', { paddingY: 'md', align: 'center', maxWidth: '820px', motion: 'fade-up' }, [
        b('ui.title', { text: 'Feito para quem precisa de resultado', size: 'lg', level: 'h2', align: 'center' }),
        b('ui.paragraph', { text: 'Explique em uma frase o que a próxima seção mostra.', align: 'center', maxWidth: '620px' }),
      ]),
    ],
  },
  {
    key: 'titulo.com-chapeu',
    category: 'titulos',
    label: 'Título com chapéu',
    description: 'Selo pequeno acima do título.',
    preview: 'heading',
    outputs: WEB,
    order: 3,
    build: () => [
      b('section.layered', { paddingY: 'md', align: 'center', maxWidth: '820px', motion: 'fade' }, [
        b('ui.badge', { text: 'Diferenciais', variant: 'soft', align: 'center' }),
        b('ui.title', { text: 'Por que escolher a nossa solução', size: 'lg', level: 'h2', align: 'center' }),
      ]),
    ],
  },
  {
    key: 'titulo.esquerda',
    category: 'titulos',
    label: 'Título alinhado à esquerda',
    description: 'Abertura alinhada à esquerda, com apoio.',
    preview: 'heading',
    outputs: WEB,
    order: 4,
    build: () => [
      b('section.layered', { paddingY: 'md', align: 'left', maxWidth: '900px', motion: 'fade-up' }, [
        b('ui.title', { text: 'O que você recebe', size: 'lg', level: 'h2' }),
        b('ui.paragraph', { text: 'Um resumo curto do que vem a seguir.' }),
      ]),
    ],
  },
  {
    key: 'titulo.com-destaque',
    category: 'titulos',
    label: 'Título com palavra em destaque',
    description: 'Parte do título recebe cor de realce.',
    preview: 'heading',
    outputs: WEB,
    order: 5,
    build: () => [
      b('section.layered', { paddingY: 'md', align: 'center', maxWidth: '860px', motion: 'fade' }, [
        b('ui.title', { text: 'Mais vendas com menos esforço', highlight: 'menos esforço', size: 'xl', level: 'h2', align: 'center' }),
      ]),
    ],
  },
  {
    key: 'titulo.escuro',
    category: 'titulos',
    label: 'Título em faixa escura',
    description: 'Abertura em bloco escuro com brilho.',
    preview: 'heading',
    outputs: WEB,
    order: 6,
    build: () => [
      b(
        'section.layered',
        { paddingY: 'lg', align: 'center', maxWidth: '900px', backgroundGradient: 'mesh-dark', glow: 'center', textColor: '#ffffff', motion: 'fade-up' },
        [b('ui.title', { text: 'Resultados que falam por si', size: 'lg', level: 'h2', align: 'center', accentColor: '#7dd3fc' })],
      ),
    ],
  },
  {
    key: 'titulo.com-divisor',
    category: 'titulos',
    label: 'Título com divisor',
    description: 'Linha discreta separando do conteúdo anterior.',
    preview: 'heading',
    outputs: WEB,
    order: 7,
    build: () => [
      b('layout.divider', { color: '#e5e7eb', thickness: '1px' }),
      b('section.layered', { paddingY: 'md', align: 'center', maxWidth: '820px' }, [
        b('ui.title', { text: 'Continue lendo', size: 'md', level: 'h2', align: 'center' }),
      ]),
    ],
  },
  {
    key: 'titulo.suave',
    category: 'titulos',
    label: 'Título em fundo suave',
    description: 'Faixa em cinza claro com cantos arredondados.',
    preview: 'heading',
    outputs: WEB,
    order: 8,
    build: () => [
      b('section.layered', { paddingY: 'md', align: 'center', maxWidth: '900px', backgroundColor: '#f8fafc', radius: 'xl', motion: 'fade' }, [
        b('ui.title', { text: 'Conheça cada detalhe', size: 'lg', level: 'h2', align: 'center' }),
      ]),
    ],
  },
  {
    key: 'titulo.duplo',
    category: 'titulos',
    label: 'Título e subtítulo',
    description: 'Título grande com subtítulo em nível menor.',
    preview: 'heading',
    outputs: WEB,
    order: 9,
    build: () => [
      b('section.layered', { paddingY: 'md', align: 'center', maxWidth: '860px', motion: 'fade-up' }, [
        b('ui.title', { text: 'Nossa proposta de valor', size: 'xl', level: 'h2', align: 'center' }),
        b('ui.title', { text: 'Tecnologia, gente e processo no mesmo lugar', size: 'md', level: 'h3', align: 'center' }),
      ]),
    ],
  },
  {
    key: 'titulo.completo',
    category: 'titulos',
    label: 'Abertura completa',
    description: 'Chapéu, título em destaque, apoio e botão.',
    preview: 'heading',
    outputs: WEB,
    order: 10,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '900px', pattern: 'dots', motion: 'fade' }, [
        b('ui.badge', { text: 'Novidade', variant: 'outline', align: 'center' }),
        b('ui.title', { text: 'Um jeito mais simples de vender', highlight: 'mais simples', size: 'xl', level: 'h2', align: 'center' }),
        b('ui.paragraph', { text: 'Menos planilhas, mais conversas que viram contrato.', align: 'center', maxWidth: '580px' }),
        b('ui.buttons', { items: 'Ver como funciona|#etapas|outline', align: 'center' }),
      ]),
    ],
  },
];

registerPatterns(modelos);
